import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import type { BookingProposalRecord } from '@navio/nsdk';
import { cva } from 'noddi-ui-common';
import {
  FieldErrorMessage,
  NoddiButton,
  NoddiLink,
  NoddiSelectableTile,
  NoddiSkeleton,
  noddiToast
} from 'noddi-ui-web';
import { customerRoutes } from 'noddi-util';
import { commonTrackingEvents, tracking, useAuthContext, useReserveStationarySlot } from 'noddi-web';
import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProposalStationaryGarage } from '../../hooks/useProposalStationaryGarage';
import { useSuggestedProposalTimes, type SuggestedProposalTime } from '../../hooks/useSuggestedProposalTimes';
import {
  useBookingProposalActions,
  useBookingProposalAnalytics,
  useBookingProposalStationaryReservation
} from '../../stores/bookingProposalStore';
import { getStationaryProposalNextPathAfterTimeChosen } from '../../utils/stationaryProposalSteps';

type Props = {
  proposal: BookingProposalRecord;
  /** Rendered beside "Pick another time" — the proposal's own way out, e.g. rejecting it. */
  secondaryAction?: ReactNode;
};

// Radio ring rather than a checkmark: the border thickens inward against a white
// center, so the dot reads as filled without a second element to color.
const timeRadioVariants = cva('size-5 shrink-0 rounded-full bg-card transition-[border-color,border-width]', {
  variants: {
    selected: {
      true: 'border-6 border-card-selected-background',
      false: 'border-[1.5px] border-border'
    }
  }
});

/**
 * Three pre-picked times on the proposal landing card, none selected up front.
 * The bay is deliberately not held while the customer reads — the reservation is
 * taken on confirm, and a lost race sends them back to a refreshed set.
 */
export const ProposalSuggestedTimes = ({ proposal, secondaryAction }: Props) => {
  const { slug } = proposal;
  const navigate = useNavigate();
  const { isLoggedIn } = useAuthContext();
  const { setStationaryReservation } = useBookingProposalActions();
  const reservation = useBookingProposalStationaryReservation();
  const { reserveSlot } = useReserveStationarySlot();
  const [pickedSlotId, setPickedSlotId] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);

  const { garageId, salesItemIds, isLoading: isGarageLoading, isMissingGarage } = useProposalStationaryGarage(proposal);
  const { suggestions, isPending, error } = useSuggestedProposalTimes({
    proposal,
    garageId,
    salesItemIds,
    enabled: !isGarageLoading
  });

  const analytics = useBookingProposalAnalytics();
  const numSuggestions = suggestions.length;
  const areSuggestionsResolved = !isGarageLoading && !isPending && !isMissingGarage;
  // The denominator for "picked a pre-chosen time vs went to the calendar".
  // A resolved-but-empty set counts: those customers are sent to the calendar
  // with no choice, which is not the same as turning the suggestions down.
  useEffect(() => {
    if (!areSuggestionsResolved) {
      return;
    }
    tracking.track(commonTrackingEvents.proposalSuggestedTimesViewed, { ...analytics, numSuggestions });
  }, [analytics, areSuggestionsResolved, numSuggestions]);

  const trackTimePickerOpened = () =>
    tracking.track(commonTrackingEvents.proposalTimePickerOpened, { ...analytics, from: 'suggestedTimes' });

  // Every branch below ends in this row, so the way out of the proposal cannot vanish
  // with a missing garage or an empty suggestion set. Picking another time is a
  // secondary flow action; rejecting the proposal remains a low-emphasis exit.
  const actionRow = ({ withPickAnotherTime }: { withPickAnotherTime: boolean }) => {
    if (!withPickAnotherTime && !secondaryAction) {
      return null;
    }

    return (
      <div className='flex flex-wrap items-center justify-center gap-4'>
        {withPickAnotherTime && (
          <NoddiLink
            variant='outline'
            size='small'
            startIcon='Clock'
            to={customerRoutes.stationaryProposalTimeWindow.getPath({ slug })}
            onClick={trackTimePickerOpened}
          >
            <Trans>Pick another time</Trans>
          </NoddiLink>
        )}
        {secondaryAction}
      </div>
    );
  };

  if (isMissingGarage) {
    return (
      <div className='flex flex-col gap-4'>
        <FieldErrorMessage
          errorMessage={t`Could not find a service center garage for this proposal. Please contact support.`}
        />
        {actionRow({ withPickAnotherTime: false })}
      </div>
    );
  }

  if (isGarageLoading || isPending) {
    return (
      <div className='flex flex-col gap-4'>
        <NoddiSkeleton className='h-5 w-2/3' />
        {Array.from({ length: 3 }).map((_, index) => (
          <NoddiSkeleton key={index} className='h-20 w-full rounded-xl' />
        ))}
        {actionRow({ withPickAnotherTime: false })}
      </div>
    );
  }

  // No capacity in the suggestion window is not an error — the full calendar
  // reaches further out, so send the customer straight there without an apology.
  if (error || suggestions.length === 0) {
    return (
      <div className='flex flex-col gap-4'>
        <NoddiLink
          to={customerRoutes.stationaryProposalTimeWindow.getPath({ slug })}
          variant='primary'
          className='w-full'
          startIcon='Clock'
          onClick={trackTimePickerOpened}
        >
          <Trans>Select time</Trans>
        </NoddiLink>
        {actionRow({ withPickAnotherTime: false })}
      </div>
    );
  }

  // Coming back to this step with a time already held shows it selected, so the
  // page reflects the booking rather than looking like nothing was chosen.
  const heldSuggestion = suggestions.find(
    (suggestion) => suggestion.slot.start === reservation?.startsAt && suggestion.slot.end === reservation.endsAt
  );
  const selectedSlotId = pickedSlotId ?? heldSuggestion?.slot.id ?? null;
  const selected = suggestions.find((suggestion) => suggestion.slot.id === selectedSlotId);

  const confirmTime = async ({ slot }: SuggestedProposalTime) => {
    if (garageId == null || slot.workstationId == null) {
      return;
    }

    setIsConfirming(true);
    try {
      const hold = await reserveSlot({
        start: slot.start,
        end: slot.end,
        garageId,
        salesItemIds: salesItemIds?.length ? salesItemIds : undefined,
        workstationId: slot.workstationId,
        // Re-picking after coming back must release the old bay, not orphan it.
        previousHold: reservation
          ? { slotId: reservation.slotId, ticket: reservation.ticket, secret: reservation.secret }
          : null,
        onPreviousHoldLost: () => setStationaryReservation(null)
      });
      setStationaryReservation(hold);
      tracking.track(commonTrackingEvents.proposalTimeSelected, { ...analytics, method: 'suggested' });
      // Login is its own step here, so the summary stays a pure review-and-pay page.
      navigate(getStationaryProposalNextPathAfterTimeChosen({ slug, isLoggedIn }));
    } catch {
      // Nothing was held while they read, so someone else may have taken the bay.
      // The three times stay put — re-picking the trio under the customer's finger
      // reads as the page breaking, so only the toast reacts to the lost race.
      setPickedSlotId(null);
      noddiToast.error(t`That time was just taken. Please choose another one.`);
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <div className='flex flex-col gap-4'>
      <h3 className='text-lg font-semibold'>
        <Trans>Choose the time that suits you</Trans>
      </h3>

      <div className='flex flex-col gap-3'>
        {suggestions.map((suggestion) => {
          const isSelected = suggestion.slot.id === selectedSlotId;

          return (
            <NoddiSelectableTile
              key={suggestion.slot.id}
              align='center'
              selected={isSelected}
              showCheckmark={false}
              disabled={isConfirming}
              onClick={() => setPickedSlotId(suggestion.slot.id)}
              className='gap-4'
            >
              <span className={timeRadioVariants({ selected: isSelected })} />
              {/* One line from sm, date left and time right: stacked, three two-line tiles
                  filled a desktop screen on their own and pushed the button below the fold.
                  Three columns is not the alternative — Norwegian weekday-plus-month labels
                  wrap inside a third of the card. */}
              <span className='flex flex-1 flex-col sm:flex-row sm:items-baseline sm:justify-between sm:gap-4'>
                <span className='font-semibold'>{suggestion.dateLabel}</span>
                <span className='text-sm text-muted-foreground'>{suggestion.timeLabel}</span>
              </span>
            </NoddiSelectableTile>
          );
        })}
      </div>

      {/* "Continue", not "Confirm": picking a time only holds the bay and moves on, and
          the step that actually books the car ends in a Confirm of its own. */}
      <NoddiButton
        className='w-full'
        endIcon='ArrowRight'
        disabled={!selected}
        loading={isConfirming}
        onClick={() => selected && void confirmTime(selected)}
      >
        <Trans>Continue</Trans>
      </NoddiButton>

      {actionRow({ withPickAnotherTime: true })}
    </div>
  );
};
